import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import getHostPickerActive from 'editor/src/store/editor/selector/getHostPickerActive';
import { useSelector } from 'editor/src/store/hooks';

import useDragging from './useDragging';

import styles from './index.module.scss';

interface Props {
  children: React.ReactNode;
  isOpen: boolean;
  addBackdrop: boolean;
  isHidden: boolean;
  close: () => void;
  maxHeight?: number;
  flexLayout?: boolean;
}

function MobileMenu({ children, isOpen, addBackdrop, isHidden, close, maxHeight, flexLayout }: Props) {
  const isVisible = useRef(false);
  const firstMount = useRef(false);
  const [changing, setChanging] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // When a host-driven file picker session is in flight, make the entire
  // menu inert (`pointer-events: none`). iOS WKWebView dispatches synthetic
  // touch events to the page when a Flutter modal (PhotoSourceChooserSheet,
  // image picker) dismisses on top of it; those touches can hit ANY clickable
  // element inside the menu — the invisible `<div .back onClick={close}>`,
  // the drag handle, OR a gallery image tile whose onClick fires
  // `closeGalleryMenuCb` after applying the image. Gating individual
  // handlers misses the gallery tiles. A blanket `pointer-events: none`
  // on the menu root catches them all in one go.
  //
  // Flag is set by `useUploader.tsx` host branch and reset on
  // `uploader.uploadSuccess` / `uploader.uploadError` /
  // `uploader.hostPickerCancelled` (see PostMessageReceiver). When
  // `UploaderConfig.useHostFilePicker` is unset, the flag stays false and
  // this behaves exactly as before.
  const hostPickerActive = useSelector(getHostPickerActive);

  useEffect(() => {
    if (!firstMount.current) {
      firstMount.current = true;
      return undefined;
    }

    isVisible.current = isOpen;
    setChanging(true);
    const handle = window.setTimeout(() => {
      setChanging(false);
    }, 150);
    return () => window.clearTimeout(handle);
  }, [isOpen]);

  const { dragging, onHandleDown } = useDragging(menuRef, close);

  if (!isOpen && !changing && !isVisible.current) {
    return null;
  }

  return (
    <div
      className={cn(styles.MobileMenu, 'mobileMenu', {
        [styles.notShown]: !isVisible.current,
        [styles.hidden]: isHidden,
      })}
      style={hostPickerActive ? { pointerEvents: 'none' } : undefined}
    >
      {addBackdrop ? (
        <div className={cn(styles.backdrop, 'cy-backdrop')} onClick={close} />
      ) : (
        <div className={styles.back} onClick={close} />
      )}
      <div className={cn(styles.menu, { [styles.dragging]: dragging })} ref={menuRef}>
        <div className={styles.menuHandleZone} onPointerDown={onHandleDown}>
          <div className={styles.menuHandle} />
        </div>
        <div className={cn(styles.menuContent, { [styles.flex]: flexLayout })} style={{ height: maxHeight }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default MobileMenu;
